import { create } from 'zustand';
import {
  DEFAULT_AVATAR_INTEGRATION_STATE,
  DEFAULT_AVATAR_CONTROLS_STATE,
  type AvatarIntegrationState,
  type AvatarControlsState,
  type AvatarOutputMode,
  type AvatarAutonomyMode,
  type AvatarPlacementPreset,
  type OverlayLayerKind,
} from '@shared/types';
import { getAvatarRenderer } from './container';

interface AvatarIntegrationActions {
  setOverlayEnabled: (enabled: boolean) => void;
  setOutputMode: (mode: AvatarOutputMode) => void;
  reorderOverlay: (id: OverlayLayerKind, direction: 'up' | 'down') => void;
  connect: () => Promise<void>;
}

interface AvatarControlsActions {
  setControlsOverlayEnabled: (enabled: boolean) => void;
  setAutonomy: (mode: AvatarAutonomyMode) => void;
  setPlacement: (preset: AvatarPlacementPreset) => void;
  setScale: (percent: number) => void;
  setRotation: (degrees: number) => void;
}

type AvatarStore = {
  integration: AvatarIntegrationState;
  controls: AvatarControlsState;
} & AvatarIntegrationActions &
  AvatarControlsActions;

/**
 * UI state for the two Avatar panels shown in the Preview page. None of
 * these actions reach a real Unity process this milestone — `connect()`
 * goes through `AvatarRendererPort` (currently `NotImplementedAvatarRenderer`)
 * so the call site never has to change when a real bridge is added.
 */
// append get param
export const useAvatarStore = create<AvatarStore>((set) => ({
  integration: DEFAULT_AVATAR_INTEGRATION_STATE,
  controls: DEFAULT_AVATAR_CONTROLS_STATE,

  setOverlayEnabled: (enabled) =>
    set((state) => ({ integration: { ...state.integration, overlayEnabled: enabled } })),

  setOutputMode: (mode) =>
    set((state) => ({ integration: { ...state.integration, outputMode: mode } })),

  reorderOverlay: (id, direction) =>
    set((state) => {
      const order = [...state.integration.overlayOrder].sort((a, b) => a.order - b.order);
      const index = order.findIndex((layer) => layer.id === id);
      const targetIndex = direction === 'up' ? index - 1 : index + 1;
      if (index === -1 || targetIndex < 0 || targetIndex >= order.length) {
        return state;
      }
      const reordered = [...order];
      [reordered[index], reordered[targetIndex]] = [reordered[targetIndex], reordered[index]];
      const withOrder = reordered.map((layer, i) => ({ ...layer, order: i + 1 }));
      return { integration: { ...state.integration, overlayOrder: withOrder } };
    }),

  connect: async () => {
    const renderer = getAvatarRenderer();
    set((state) => ({ integration: { ...state.integration, status: 'connecting' } }));
    await renderer.connect();
    set((state) => ({ integration: { ...state.integration, status: renderer.getStatus() } }));
  },

  setControlsOverlayEnabled: (enabled) =>
    set((state) => ({ controls: { ...state.controls, overlayEnabled: enabled } })),

  setAutonomy: (mode) => set((state) => ({ controls: { ...state.controls, autonomy: mode } })),

  setPlacement: (preset) =>
    set((state) => ({ controls: { ...state.controls, placement: preset } })),

  setScale: (percent) =>
    set((state) => ({ controls: { ...state.controls, scalePercent: percent } })),

  setRotation: (degrees) =>
    set((state) => ({ controls: { ...state.controls, rotationDegrees: degrees } })),
}));

// Re-exported for cases where a component needs the raw snapshot outside React render.
export const getAvatarState = () => useAvatarStore.getState();
